import { SdkContext } from '../internal'
import type {
  ApiKey,
  ApiKeyModules,
  ApiKeyStatus,
  ApiKeyUsageMonth,
  CommunityUsageMonth,
  CreateApiKeyBody,
  CreatedApiKey,
  IntegrationAccessModel,
  IntegrationQuota
} from '@/types/apiKeys'

const asList = <T>(result: unknown): T[] =>
  Array.isArray(result) ? (result as T[]) : ((result as { items?: T[] } | null)?.items || [])

/**
 * F-101 1a: Integrações & APIs. The community is the X-CommunityId's; these
 * routes take the session only (no API key reaches them).
 */
export class IntegrationsResource {
  constructor(private ctx: SdkContext) {}

  /** Every module with its translated label, the three levels and the limits. */
  async modules(): Promise<ApiKeyModules> {
    return this.ctx.call({ httpMethod: 'GET', path: '/integrations/api-keys/modules' })
  }

  /** The caller's own keys, newest first. */
  async listMine(): Promise<ApiKey[]> {
    return asList<ApiKey>(await this.ctx.raw({ httpMethod: 'GET', path: '/integrations/api-keys' }))
  }

  /** Every key of the community: owner and admin; anyone else 403. */
  async listCommunity(params: { memberId?: string; status?: ApiKeyStatus } = {}): Promise<ApiKey[]> {
    const query = Object.fromEntries(Object.entries(params).filter(([, v]) => v))
    return asList<ApiKey>(await this.ctx.raw({ httpMethod: 'GET', path: '/integrations/api-keys/community' }, { query }))
  }

  /** Creates a key; the answer carries `secret`, the only time it is ever returned. At most 5 active keys per person. */
  async create(body: CreateApiKeyBody): Promise<CreatedApiKey> {
    return this.ctx.call({ httpMethod: 'POST', path: '/integrations/api-keys', hasBody: true }, { body })
  }

  async get(id: string): Promise<ApiKey> {
    return this.ctx.call({ httpMethod: 'GET', path: '/integrations/api-keys/{id}' }, { id })
  }

  /** Renames and/or replaces the allowed IPs. Only the key's owner; scopes never change. */
  async update(id: string, body: { name?: string; allowedIps?: string[] }): Promise<ApiKey> {
    return this.ctx.call({ httpMethod: 'PATCH', path: '/integrations/api-keys/{id}', hasBody: true }, { id, body })
  }

  /** The key's owner, or an owner/admin of the community. Takes effect on the next call. */
  async revoke(id: string): Promise<ApiKey> {
    return this.ctx.call({ httpMethod: 'POST', path: '/integrations/api-keys/{id}/revoke', hasBody: true }, { id })
  }

  /** Calls of one key: this month and the six before, newest first. */
  async keyUsage(id: string): Promise<ApiKeyUsageMonth[]> {
    const data = await this.ctx.call<{ months?: ApiKeyUsageMonth[] }>({ httpMethod: 'GET', path: '/integrations/api-keys/{id}/usage' }, { id })
    return data?.months || []
  }

  /** The community's usage (owner and admin): this month and the six before, by key and owner. */
  async communityUsage(): Promise<CommunityUsageMonth[]> {
    const data = await this.ctx.call<{ months?: CommunityUsageMonth[] }>({ httpMethod: 'GET', path: '/integrations/usage' })
    return data?.months || []
  }

  /* SuperAdmin, acting on another community (the id is the target, not the context). */

  async getQuota(communityId: string): Promise<IntegrationQuota> {
    return this.ctx.call({ httpMethod: 'GET', path: '/admin/communities/{communityId}/integration-quota' }, { communityId })
  }

  /** `null` clears a field; a field left out stays. */
  async setQuota(
    communityId: string,
    body: { apiCallsPerMonth?: number | null; webhooksPerMonth?: number | null; accessModel?: IntegrationAccessModel | null }
  ): Promise<IntegrationQuota> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/admin/communities/{communityId}/integration-quota', hasBody: true }, { communityId, body })
  }
}
